import { type HTMLAttributes, type ReactElement, useState } from 'react'
import { DialogHint } from '@/features/marquee/components/dialog-hint'
import {
	ResponsiveConfirmDialog,
	ResponsiveConfirmTrigger
} from '@/features/marquee/components/responsive-confirm-dialog'
import {
	resolutionWorklistFilename,
	saveCsv,
	toResolutionWorklistCsv
} from '@/features/marquee/utils'
import type { MarqueeAttendee, MarqueeEventDetail } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const WORKLIST_STEP_KEYS = [
	'marquee:resolution_worklist_dialog_step_1',
	'marquee:resolution_worklist_dialog_step_2',
	'marquee:resolution_worklist_dialog_step_3',
	'marquee:resolution_worklist_dialog_step_4'
] as const

interface ResolutionWorklistDialogProps {
	event: MarqueeEventDetail
	/** The rows the file lists: [[resolutionWorklistRows]], already filtered. */
	rows: MarqueeAttendee[]
	/** Element that opens the confirmation (the "Download account worklist" button). */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
}

/**
 * "Download the account worklist?" confirmation, in front of a download that
 * needs no confirming on its own. It is there for the instructions: the file
 * lists the host IDs on this event still waiting on a Salesforce account, in
 * the lookup table's own columns, and the only correct thing to do with it is
 * paste the filled rows into the **full** table and upload that. The global
 * table is replace-all, so a per-event file uploaded on its own would retire
 * every other market's rows. The dialog says so before the file exists, and
 * the filename says it again in the folder.
 *
 * Built client-side from the roster already on screen: the file is a view of
 * the rows, not a resource of its own.
 */
function ResolutionWorklistDialog({
	event,
	rows,
	trigger
}: ResolutionWorklistDialogProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)

	const download = () => {
		saveCsv(
			resolutionWorklistFilename(event),
			toResolutionWorklistCsv(rows)
		)
		setOpen(false)
	}

	return (
		<ResponsiveConfirmDialog
			body={
				<>
					<ol className='flex list-decimal flex-col gap-1 pl-5 text-neutral-500 text-sm'>
						{WORKLIST_STEP_KEYS.map((key) => (
							<li key={key}>{t(key)}</li>
						))}
					</ol>
					<DialogHint
						description={t(
							'marquee:resolution_worklist_dialog_warning_body'
						)}
						title={t(
							'marquee:resolution_worklist_dialog_warning_title'
						)}
					/>
				</>
			}
			confirmLabel={t('marquee:resolution_worklist_dialog_confirm')}
			description={t('marquee:resolution_worklist_dialog_description', {
				count: rows.length
			})}
			onConfirm={download}
			onOpenChange={setOpen}
			open={open}
			title={t('marquee:resolution_worklist_dialog_title')}
		>
			<ResponsiveConfirmTrigger>{trigger}</ResponsiveConfirmTrigger>
		</ResponsiveConfirmDialog>
	)
}

export { ResolutionWorklistDialog }
